import React from 'react';
import { ComposedChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';

const UP = '#10b981';   // Optimus emerald green
const DOWN = '#f43f5e'; // Optimus rose red

function Candle({ x, y, width, height, payload }) {
  const { open, close, high, low } = payload;
  if (high == null || low == null || high === low || height <= 0) return null;
  const scale = height / (high - low);
  const openY = y + (high - open) * scale;
  const closeY = y + (high - close) * scale;
  const bodyTop = Math.min(openY, closeY);
  const bodyHeight = Math.max(Math.abs(closeY - openY), 1);
  const isUp = close >= open;
  const color = isUp ? UP : DOWN;
  const bodyWidth = Math.max(width * 0.6, 2);
  const bodyX = x + (width - bodyWidth) / 2;
  const wickX = x + width / 2;
  return (
    <g>
      <line x1={wickX} x2={wickX} y1={y} y2={y + height} stroke={color} strokeWidth={1} />
      <rect x={bodyX} y={bodyTop} width={bodyWidth} height={bodyHeight} fill={color} />
    </g>
  );
}

function CandleTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const pctColor = d.pct_change == null ? 'text-gray-400' : d.pct_change >= 0 ? 'text-emerald-400' : 'text-rose-400';
  return (
    <div className="bg-[#14171f] border border-gray-800 rounded-lg px-3 py-2 text-xs font-mono shadow-xl min-w-[150px]">
      <div className="flex items-baseline justify-between mb-1.5 gap-3 border-b border-gray-800/80 pb-1">
        <span className="text-gray-400 font-sans text-[11px]">{label}</span>
        <span className={`font-semibold ${pctColor}`}>
          {d.pct_change == null ? '—' : `${d.pct_change >= 0 ? '+' : ''}${d.pct_change.toFixed(2)}%`}
        </span>
      </div>
      <div className="flex justify-between text-gray-300"><span className="text-gray-500 font-sans">Open</span> {d.open?.toFixed(2)}</div>
      <div className="flex justify-between text-gray-300"><span className="text-gray-500 font-sans">High</span> {d.high?.toFixed(2)}</div>
      <div className="flex justify-between text-gray-300"><span className="text-gray-500 font-sans">Low</span> {d.low?.toFixed(2)}</div>
      <div className="flex justify-between font-semibold pt-0.5 border-t border-gray-800/50 mt-0.5">
        <span className="text-gray-400 font-sans font-normal">Close</span>
        <span className={d.close >= d.open ? 'text-emerald-400' : 'text-rose-400'}>{d.close?.toFixed(2)}</span>
      </div>
    </div>
  );
}

function CrosshairCursor({ points }) {
  if (!points?.length) return null;
  const [top, bottom] = points;
  return <line x1={top.x} x2={top.x} y1={top.y} y2={bottom.y} stroke="#4b5563" strokeDasharray="3 3" />;
}

export default function CandlestickChart({ data }) {
  const chartData = (data || []).map((d, i) => {
    const prevClose = i > 0 ? data[i - 1].close : null;
    const pct_change = prevClose ? ((d.close - prevClose) / prevClose) * 100 : null;
    return { ...d, range: [d.low, d.high], pct_change };
  });

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={chartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid stroke="#1f2937" strokeDasharray="2 4" vertical={false} />
        <XAxis dataKey="date" stroke="#6b7280" tick={{ fontSize: 11, fontFamily: 'monospace' }}
               tickLine={false} axisLine={{ stroke: '#374151' }} minTickGap={40} />
        <YAxis stroke="#6b7280" tick={{ fontSize: 11, fontFamily: 'monospace' }} domain={['auto', 'auto']}
               orientation="right" tickLine={false} axisLine={{ stroke: '#374151' }}
               tickFormatter={(v) => v.toFixed(0)} />
        <Tooltip content={<CandleTooltip />} cursor={<CrosshairCursor />} />
        <Bar dataKey="range" shape={<Candle />} isAnimationActive={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
